import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { formatDate, createCommonStyles, formatTagName, formatKeyName } from '../utils/pdfUtils';

// Create styles for the PDF specific to canvass details
const styles = createCommonStyles({
  sectionHeader: {
    backgroundColor: '#e0e7ff',
    paddingTop: 12,
    paddingBottom: 12,
    paddingLeft: 20,
    paddingRight: 20,
    marginBottom: 12,
    marginTop: 18,
    borderRadius: 8,
    borderLeft: '4pt solid #1e40af',
  },
  sectionHeaderText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1e40af',
  },
  keyValueRow: {
    marginBottom: 6,
  },
  keyText: {
    fontSize: 11,
    color: '#374151',
    fontWeight: 'bold',
    marginRight: 6,
  },
  valueText: {
    fontSize: 11,
    color: '#1f2937',
  },
  badgeItem: {
    marginBottom: 6,
  },
  bulletItem: {
    fontSize: 11,
    color: '#1f2937',
    marginBottom: 4,
  },
  // Minimal message/thread styles
  messageContainer: {
    marginBottom: 12,
    paddingBottom: 10,
    paddingLeft: 12,
    borderBottom: '1pt solid #e5e7eb',
    borderLeft: '3pt solid #e5e7eb',
  },
  messageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  messageRole: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#374151',
    textTransform: 'uppercase',
  },
  messageTime: {
    fontSize: 9,
    color: '#9ca3af',
  },
  messageContent: {
    fontSize: 11,
    color: '#1f2937',
    lineHeight: 1.5,
  },
});

const KeyValue = ({ label, value }) => (
  <View style={styles.keyValueRow}>
    <Text style={styles.keyText}>{label}:</Text>
    <Text style={styles.valueText}>{value || '—'}</Text>
  </View>
);

const SectionHeader = ({ children }) => (
  <View style={styles.sectionHeader}>
    <Text style={styles.sectionHeaderText}>{children}</Text>
  </View>
);

// New PDF for Canvass details
// Props: { selectedConversation: canvassObject }
const ChatHistoryPDFDocumentCanvass = ({ selectedConversation }) => {
  const c = selectedConversation || {};
  const currentDate = new Date().toLocaleString();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>August Says Canvass Details</Text>
          <Text style={styles.subtitle}>Generated on: {currentDate}</Text>
        </View>

        {/* Top summary */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{c.canvassName || 'Untitled Canvass'}</Text>
          <KeyValue label="Canvass ID" value={c.canvassId || c.id} />
          {c.timestamp && (
            <KeyValue label="Created" value={formatDate(c.timestamp)} />
          )}
          <KeyValue label="Status" value={c.status} />
          {c.interviewLink && (
            <KeyValue label="Interview Link" value={c.interviewLink} />
          )}
        </View>

        {/* Description */}
        {c.canvassDescription && (
          <View style={styles.section}>
            <SectionHeader>Description</SectionHeader>
            <Text style={styles.bodyText}>{c.canvassDescription}</Text>
          </View>
        )}

        {/* Objectives */}
        {(c.objective || (Array.isArray(c.sub_objectives) && c.sub_objectives.length)) && (
          <View style={styles.section}>
            <SectionHeader>Objectives</SectionHeader>
            {c.objective && <KeyValue label="Primary Objective" value={c.objective} />}
            {Array.isArray(c.sub_objectives) && c.sub_objectives.length > 0 && (
              <View>
                <Text style={styles.keyText}>Sub-objectives:</Text>
                {c.sub_objectives.map((item, idx) => (
                  <Text key={`so-${idx}`} style={styles.bulletItem}>• {String(item)}</Text>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Audience / Environment */}
        {(c.audience || c.environment) && (
          <View style={styles.section}>
            <SectionHeader>Context</SectionHeader>
            {c.audience && <KeyValue label="Audience" value={c.audience} />}
            {c.environment && <KeyValue label="Environment" value={c.environment} />}
          </View>
        )}

        {/* Touchpoints */}
        {Array.isArray(c.touchpoints) && c.touchpoints.length > 0 && (
          <View style={styles.section}>
            <SectionHeader>Touchpoints</SectionHeader>
            {c.touchpoints.map((tp, idx) => (
              <Text key={`tp-${idx}`} style={styles.bulletItem}>• {String(tp)}</Text>
            ))}
          </View>
        )}

        {/* Observed Behaviors / External Context */}
        {(c.observed_behaviors || c.external_context) && (
          <View style={styles.section}>
            <SectionHeader>Observations</SectionHeader>
            {c.observed_behaviors && (
              <KeyValue label="Observed Behaviors" value={c.observed_behaviors} />
            )}
            {c.external_context && (
              <KeyValue label="External Context" value={c.external_context} />
            )}
          </View>
        )}

        {/* Constraints */}
        {c.constraints && (c.constraints.tone || c.constraints.timebox || c.constraints.incentive) && (
          <View style={styles.section}>
            <SectionHeader>Constraints</SectionHeader>
            {c.constraints.tone ? <KeyValue label="Tone" value={c.constraints.tone} /> : null}
            {c.constraints.timebox ? <KeyValue label="Timebox" value={c.constraints.timebox} /> : null}
            {c.constraints.incentive ? <KeyValue label="Incentive" value={c.constraints.incentive} /> : null}
          </View>
        )}

        {/* Interview Introductions */}
        {Array.isArray(c.interview_introductions) && c.interview_introductions.length > 0 && (
          <View style={styles.section}>
            <SectionHeader>Interview Introductions</SectionHeader>
            {c.interview_introductions.map((intro, idx) => (
              <View key={`intro-${idx}`} style={{ marginBottom: 10 }}>
                {intro.introduction_text && (
                  <KeyValue label={`Introduction ${idx + 1}`} value={intro.introduction_text} />
                )}
                {intro.badge_theme && (
                  <KeyValue label="Badge Theme" value={intro.badge_theme} />
                )}
                {Array.isArray(intro.sample_badges) && intro.sample_badges.length > 0 && (
                  <View>
                    <Text style={styles.keyText}>Sample Badges:</Text>
                    {intro.sample_badges.map((b, j) => (
                      <Text key={`badge-${idx}-${j}`} style={styles.bulletItem}>
                        • {b.badge}: {b.description}
                      </Text>
                    ))}
                  </View>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Success Metrics */}
        {c.success_metrics && (
          <View style={styles.section}>
            <SectionHeader>Success Metrics</SectionHeader>
            <Text style={styles.bodyText}>{c.success_metrics}</Text>
          </View>
        )}

        {/* Onboarding Thread */}
        {Array.isArray(c.messages) && c.messages.length > 0 && c.includeMessages && (
          <View style={styles.section}>
            <SectionHeader>Onboarding Thread</SectionHeader>
            {c.messages.map((message, idx) => {
              let content = '';
              if (typeof message?.content === 'string') {
                content = message.content;
              } else if (message?.content && typeof message.content === 'object') {
                if (message.content.introduction_text) {
                  content = message.content.introduction_text;
                } else if (message.content.badge && message.content.description) {
                  content = `${message.content.badge}: ${message.content.description}`;
                } else if (message.content.next_question) {
                  content = message.content.next_question;
                } else if (message.content.question) {
                  content = message.content.question;
                } else {
                  content = JSON.stringify(message.content);
                }
              }

              return (
                <View key={`msg-${idx}`} style={[styles.messageContainer, idx === c.messages.length - 1 && { marginBottom: 0 }] }>
                  <View style={styles.messageHeader}>
                    <Text style={styles.messageRole}>{message.role === 'user' ? 'User' : 'Assistant'}</Text>
                    <Text style={styles.messageTime}>{formatDate(message.timestamp || c.startTime)}</Text>
                  </View>
                  <Text style={styles.messageContent}>{String(content)}</Text>
                </View>
              );
            })}
          </View>
        )}

        {/* Footer */}
        <Text fixed style={styles.footerText}>August Says - Canvass Details</Text>
      </Page>
    </Document>
  );
};

export default ChatHistoryPDFDocumentCanvass;
